import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, GitFork, ArrowUpRight, Github } from "lucide-react";
import { SectionHeading, staggerContainer, staggerItem } from "./Reveal";
import { profile } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const langColor = (lang) => {
  const map = {
    JavaScript: "#e3b341",
    TypeScript: "#4a9df0",
    Python: "#7aa6da",
    PHP: "#9b8cff",
    "C#": "#7ad19b",
    HTML: "#e06a4b",
    CSS: "#6ab0ff",
    Java: "#e08c4b",
  };
  return map[lang] || "#8a8f98";
};

const RepoSkeleton = () => (
  <div className="rounded-3xl border border-[var(--border-subtle)] bg-surface p-7 md:p-8 animate-pulse">
    <div className="h-5 w-1/2 rounded bg-white/5" />
    <div className="mt-4 h-3 w-full rounded bg-white/5" />
    <div className="mt-2 h-3 w-2/3 rounded bg-white/5" />
    <div className="mt-6 h-3 w-24 rounded bg-white/5" />
  </div>
);

export const OpenSource = () => {
  const [repos, setRepos] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | done | error

  useEffect(() => {
    let active = true;
    fetch(`${API}/github/repos`)
      .then((r) => {
        if (!r.ok) throw new Error("bad status");
        return r.json();
      })
      .then((data) => {
        if (!active) return;
        setRepos(Array.isArray(data.repos) ? data.repos : []);
        setStatus("done");
      })
      .catch(() => active && setStatus("error"));
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="opensource"
      data-testid="opensource-section"
      className="relative max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32"
    >
      <SectionHeading
        index="05"
        overline="Open source"
        title="Live from GitHub."
        description="Auto-surfaced from my GitHub — highest-signal repositories first, forks and empty repos filtered out. Quality over quantity."
      />

      {status === "error" ? (
        <div
          data-testid="opensource-error"
          className="rounded-3xl border border-[var(--border-subtle)] bg-surface p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <p className="text-secondary-c">
            Couldn&apos;t load repositories right now.
          </p>
          <a
            data-testid="opensource-github-fallback"
            href={profile.github}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[var(--border-strong)] text-primary-c font-medium rounded-full px-5 py-2.5 hover:bg-white/5 transition-colors"
          >
            <Github className="w-4 h-4" /> View on GitHub
          </a>
        </div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
        >
          {status === "loading"
            ? Array.from({ length: 6 }).map((_, i) => <RepoSkeleton key={i} />)
            : repos.map((repo) => (
                <motion.a
                  key={repo.id}
                  variants={staggerItem}
                  data-testid={`repo-card-${repo.name.toLowerCase()}`}
                  href={repo.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col rounded-3xl border border-[var(--border-subtle)] bg-surface p-7 md:p-8 transition-all duration-300 hover:border-[var(--border-strong)] hover:bg-surface-elevated hover:-translate-y-1"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Github
                        className="w-4 h-4 text-tertiary-c shrink-0"
                        strokeWidth={1.5}
                      />
                      <h3 className="font-heading font-bold text-base md:text-lg tracking-tight text-primary-c truncate">
                        {repo.name}
                      </h3>
                    </div>
                    <ArrowUpRight
                      className="w-4 h-4 text-tertiary-c transition-all duration-300 group-hover:text-primary-c group-hover:-translate-y-0.5 group-hover:translate-x-0.5 shrink-0"
                      strokeWidth={1.5}
                    />
                  </div>

                  <p className="mt-3 text-sm text-secondary-c leading-relaxed line-clamp-2 flex-1">
                    {repo.description || "No description provided."}
                  </p>

                  {repo.topics?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {repo.topics.map((t) => (
                        <span
                          key={t}
                          className="font-mono-tech text-[10px] tracking-wide bg-white/[0.04] border border-white/10 text-tertiary-c rounded-full px-2 py-0.5"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-5 pt-4 border-t border-[var(--border-subtle)] flex items-center gap-5 text-xs text-secondary-c">
                    {repo.language && (
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: langColor(repo.language) }}
                        />
                        {repo.language}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {repo.stars}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <GitFork className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {repo.forks}
                    </span>
                  </div>
                </motion.a>
              ))}
        </motion.div>
      )}

      <div className="mt-10">
        <a
          data-testid="opensource-view-all"
          href={profile.github}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-medium text-secondary-c hover:text-primary-c transition-colors"
        >
          View all repositories on GitHub
          <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
        </a>
      </div>
    </section>
  );
};
